import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { Logger } from '../types/logger'
import { KibsiClientParams } from './params'

/**
 * Client to make API calls to Kibsi services.
 * Implement per entity type by defining the REST paths.
 */
export abstract class KibsiClient {
    private logger?: Logger
    private servicePath: string
    private baseURL: string

    /**
     * The Axios instance.
     * Use instanceWithLatestConfig() to ensure it is appropriately configured.
     */
    private instance: AxiosInstance

    constructor(params: KibsiClientParams, servicePath: string) {
        this.logger = params.logger
        this.servicePath = servicePath
        this.baseURL = params.basePathTemplate.replace('{service}', this.servicePath)
        this.logger?.debug(`Client baseURL: ${this.baseURL}`)

        this.instance = axios.create({
            baseURL: this.baseURL,
            timeout: 10_000,
        })

        params.interceptors?.request?.forEach((req) => {
            this.instance.interceptors.request.use(...req)
        })

        params.interceptors?.response?.forEach((resp) => {
            this.instance.interceptors.response.use(...resp)
        })
    }

    /** HTTP methods */

    protected async post<Resp = unknown, Req = unknown>(
        path: string,
        entity: Req,
        requestConfig?: AxiosRequestConfig,
    ): Promise<Resp> {
        this.logger?.debug(`${this.servicePath} client POST ${path}`)
        const result = await this.instance.post(path, entity, requestConfig)

        this.logger?.debug(`Status: ${result.status}`)
        return result.data as Resp
    }

    protected async get<Resp = unknown>(path: string, requestConfig?: AxiosRequestConfig): Promise<Resp> {
        this.logger?.debug(`${this.servicePath} client GET ${path}`)
        const result = await this.instance.get(path, requestConfig)

        this.logger?.debug(`Status: ${result.status}`)
        return result.data as Resp
    }

    protected async put<Resp = unknown, Req = unknown>(
        path: string,
        entity: Req,
        requestConfig?: AxiosRequestConfig,
    ): Promise<Resp> {
        this.logger?.debug(`${this.servicePath} client PUT ${path}`)
        const result = await this.instance.put(path, entity, requestConfig)

        this.logger?.debug(`Status: ${result.status}`)
        return result.data as Resp
    }

    protected async patch<Resp = unknown, Req = unknown>(
        path: string,
        entity: Req,
        requestConfig?: AxiosRequestConfig,
    ): Promise<Resp> {
        this.logger?.debug(`${this.servicePath} client PATCH ${path}`)
        const result = await this.instance.patch(path, entity, requestConfig)

        this.logger?.debug(`Status: ${result.status}`)
        return result.data as Resp
    }

    protected async del(path: string, requestConfig?: AxiosRequestConfig): Promise<void> {
        this.logger?.debug(`${this.servicePath} client DELETE ${path}`)
        const result = await this.instance.delete(path, requestConfig)

        this.logger?.debug(`Status: ${result.status}`)
    }
}
